import { Fragment, forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { dayKey, dayLabel } from "../lib/dates.js";
import { decryptMessage, decryptBytes, encryptMessage, encryptBytes } from "../lib/crypto.js";
import {
  sendMessage,
  runCommand,
  getAttachment,
  requestUploadUrl,
  listMessagesPage,
  editMessage,
  deleteMessage,
  getLinkPreview,
  type ApiMessage,
  type LinkPreview
} from "../lib/api.js";
import { startWs } from "../lib/ws.js";
import { renderMarkdown } from "../lib/markdown.js";
import type { Session } from "../lib/session.js";

interface ChatProps {
  session: Session;
  onMenuStateChange?: (state: { searchVisible: boolean; showAttachments: boolean }) => void;
}

/** Actions the App-level "⋮" menu (in the Chat/Settings nav bar) drives —
 * kept as an imperative handle so Chat.tsx's own state/logic for
 * search/attachments doesn't have to be lifted into App.tsx. */
export interface ChatHandle {
  toggleSearch: () => void;
  toggleAttachments: () => void;
}

interface DisplayMessage {
  id: string;
  mine: boolean;
  text: string;
  createdAt: number;
  updatedAt: number;
  attachment: ApiMessage["attachment"];
  attachmentNonce: string | null;
  /** True for a slash-command's stored result (see COMMAND_RESULT_PREFIX below) — rendered as system output, not a typed message. */
  isCommandResult: boolean;
  deleted: boolean;
  edited: boolean;
}

// Slash commands (e.g. `/ping`) dispatch over a separate, explicit,
// unencrypted channel (`POST /api/commands` — see
// `server/src/commands/registry.ts`) because the server needs to understand
// and act on them, which is incompatible with the E2EE messages pipeline.
// Once a result comes back, BOTH the original typed command text and its
// result are stored as normal encrypted messages via `sendMessage`, same as
// any other message — this prefix is a purely client-side marker (prepended
// before encryption, stripped after decryption) so the result message can be
// rendered distinctly as system output. It never appears in plaintext
// outside this client and is never sent to the server directly.
const COMMAND_RESULT_PREFIX = "\u0000bridge-cmd-result\u0000";

// Scroll-up pagination fetches the next older page just before the user
// actually reaches the top — a small threshold feels less abrupt than
// waiting for scrollTop === 0.
const SCROLL_TOP_THRESHOLD = 40;

function decodeIncoming(message: ApiMessage, session: Session): DisplayMessage {
  const updatedAt = message.updatedAt ?? message.createdAt;
  if (message.deletedAt) {
    return {
      id: message.id,
      mine: true,
      text: "",
      createdAt: message.createdAt,
      updatedAt,
      attachment: null,
      attachmentNonce: null,
      isCommandResult: false,
      deleted: true,
      edited: false
    };
  }
  let text = "";
  if (message.encrypted && message.ciphertext && message.nonce) {
    text = decryptMessage(message.ciphertext, message.nonce, session.chatKey) ?? "[unable to decrypt]";
  } else if (message.ciphertext) {
    text = message.ciphertext; // plaintext fallback, only reachable when E2EE_REQUIRED=false
  } else if (message.attachment) {
    text = "";
  }
  const isCommandResult = text.startsWith(COMMAND_RESULT_PREFIX);
  if (isCommandResult) text = text.slice(COMMAND_RESULT_PREFIX.length);
  return {
    id: message.id,
    // Every message `GET /api/messages` returns is already scoped to the
    // caller's own account (`WHERE user_id = ?`, server/routes/messages.ts)
    // — there's no cross-account messaging yet (that's the unbuilt "accepted
    // senders" feature), so anything in this chat was sent by you, whichever
    // of your own devices (PC, mobile, other) actually sent it. Comparing
    // against `session.deviceId` used to mis-render messages sent from your
    // *other* devices as "received" (left-aligned) instead of "sent". When
    // cross-account messaging ships, this should switch to comparing a real
    // senderUserId against the session's own account id.
    mine: true,
    text,
    createdAt: message.createdAt,
    updatedAt,
    attachment: message.attachment,
    attachmentNonce: message.attachment ? message.nonce : null,
    isCommandResult,
    deleted: false,
    edited: Boolean(message.editedAt)
  };
}

// First-URL-in-text detection for chat's link-preview cards — deliberately
// simple ("don't over-engineer URL detection" per the spec): the first
// http(s) URL found, stopping at whitespace.
const URL_REGEX = /https?:\/\/[^\s]+/;

function extractFirstUrl(text: string): string | null {
  const match = URL_REGEX.exec(text);
  return match ? match[0] : null;
}

// A plain regex (not the `URL` API) so this stays identical in spirit to
// mobile's equivalent helper — display-only, doesn't need real URL parsing.
function extractHostname(url: string): string {
  const match = /^https?:\/\/(?:www\.)?([^/?#]+)/i.exec(url);
  return match ? match[1] : url;
}

// --- X/Twitter tweet-card detection (progressive enhancement over the
// generic link-preview card — see LinkPreviewCard below). Never a hard
// dependency: if the hostname doesn't match or the title regex misses (X
// changes markup, quote-tweet/retweet, deleted post, etc.), the generic card
// renders exactly as it did before this existed. ---

const TWEET_HOSTNAMES = new Set(["x.com", "www.x.com", "twitter.com", "www.twitter.com"]);
const TWEET_TITLE_RE = /^(.+?) \(@([A-Za-z0-9_]+)\) on X$/;

interface TweetInfo {
  name: string;
  handle: string;
}

/** Returns parsed { name, handle } only when the URL's hostname is X/Twitter
 * AND the scraped title matches X's `"<Name> (@<handle>) on X"` convention —
 * both must hold, otherwise null (caller falls back to the generic card). */
function detectTweet(url: string, title: string | null): TweetInfo | null {
  if (!title) return null;
  let hostname: string;
  try {
    hostname = new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (!TWEET_HOSTNAMES.has(hostname)) return null;
  const match = TWEET_TITLE_RE.exec(title);
  if (!match) return null;
  return { name: match[1], handle: match[2] };
}

/**
 * Renders a WhatsApp-style link preview card below a message's text when it
 * contains a URL: a full-width image on top (when one exists), then the
 * hostname/title/description stacked underneath. Fetches `getLinkPreview()`
 * asynchronously AFTER the message text is already visible (never
 * blocks/delays rendering the text itself), and never renders anything if
 * the fetch fails or comes back all-nulls — a failed/slow preview never
 * affects the message itself. `cache` is a plain `Map` owned by the parent
 * Chat instance (per the spec) so scrolling past the same message
 * repeatedly, or several messages sharing a link, only ever fetches once per
 * URL per Chat mount.
 */
/** Remote preview image: one delayed retry (og-image hosts like GitHub's
 * render on first request and can fail it), then hidden instead of broken. */
function PreviewImage({ src, className }: { src: string; className: string }) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [src]);

  if (failed) return null;
  return (
    <img
      key={attempt}
      src={src}
      alt=""
      className={className}
      loading="lazy"
      onError={() => {
        if (attempt === 0) setTimeout(() => setAttempt(1), 1500);
        else setFailed(true);
      }}
    />
  );
}

function LinkPreviewCard({ url, cache }: { url: string; cache: Map<string, LinkPreview | null> }) {
  const [preview, setPreview] = useState<LinkPreview | null | undefined>(() => cache.get(url));

  useEffect(() => {
    if (cache.has(url)) {
      setPreview(cache.get(url) ?? null);
      return;
    }
    let cancelled = false;
    getLinkPreview(url)
      .then((result) => {
        cache.set(url, result);
        if (!cancelled) setPreview(result);
      })
      .catch(() => {
        cache.set(url, null);
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url, cache]);

  if (!preview || (!preview.title && !preview.description && !preview.image)) return null;

  // Prefer the same-origin signed proxy so ad/tracker blockers can't strip
  // third-party og:image hosts (GitHub, X, …) while leaving the card empty.
  const imageSrc = preview.imageProxy ?? preview.image;

  const tweet = detectTweet(url, preview.title);
  if (tweet) {
    const isAvatar = !!preview.image && preview.image.includes("/profile_images/");
    const isMediaPhoto = !!preview.image && preview.image.includes("/media/");
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="mt-1.5 block overflow-hidden rounded-lg border border-slate-700 bg-slate-950/60 no-underline transition-colors duration-150 hover:bg-slate-900/80"
      >
        <div className="px-2.5 py-2">
          <div className="flex items-center gap-2">
            {isAvatar && imageSrc && (
              <PreviewImage src={imageSrc} className="h-8 w-8 shrink-0 rounded-full object-cover" />
            )}
            <div className="min-w-0 truncate">
              <span className="text-xs font-semibold text-slate-100">{tweet.name}</span>
              <span className="ml-1 text-xs text-slate-500">@{tweet.handle}</span>
            </div>
          </div>
          {preview.description && (
            <div className="mt-1 line-clamp-4 whitespace-pre-line text-[0.7rem] text-slate-300">
              {preview.description}
            </div>
          )}
        </div>
        {isMediaPhoto && imageSrc && <PreviewImage src={imageSrc} className="h-40 w-full object-cover" />}
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="mt-1.5 block overflow-hidden rounded-lg border border-slate-700 bg-slate-950/60 no-underline transition-colors duration-150 hover:bg-slate-900/80"
    >
      {imageSrc && <PreviewImage src={imageSrc} className="h-40 w-full object-cover" />}
      <div className="px-2.5 py-2">
        <div className="truncate text-[0.65rem] uppercase tracking-wide text-slate-500">{extractHostname(url)}</div>
        {preview.title && (
          <div className="mt-0.5 line-clamp-2 text-xs font-semibold text-slate-100">{preview.title}</div>
        )}
        {preview.description && (
          <div className="mt-0.5 line-clamp-2 text-[0.7rem] text-slate-400">{preview.description}</div>
        )}
      </div>
    </a>
  );
}

const IMAGE_FILENAME_RE = /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i;

/** Detects an image attachment by filename extension, not `contentType` —
 * the web upload path (see `handleAttach` below) always sends
 * `application/octet-stream` regardless of the real file type, since the
 * server never inspects plaintext content type for encrypted bytes. */
function isImageFilename(filename: string): boolean {
  return IMAGE_FILENAME_RE.test(filename);
}

/** Extension → emoji, for the non-image file card below. Plain Unicode
 * glyphs, same minimal-icon convention as the rest of this app (⋮, ⧉, ↗, ➤)
 * — no icon library dependency. */
function fileIconFor(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "📄";
  if (["doc", "docx", "rtf", "odt"].includes(ext)) return "📝";
  if (["xls", "xlsx", "csv", "ods"].includes(ext)) return "📊";
  if (["ppt", "pptx", "odp"].includes(ext)) return "📽";
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return "🗜";
  if (["mp3", "wav", "m4a", "aac", "flac", "ogg"].includes(ext)) return "🎵";
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return "🎬";
  if (["txt", "md"].includes(ext)) return "📃";
  return "📎";
}

/**
 * Card for a non-image attachment: an extension-based icon, filename, and
 * size — WhatsApp-style file preview, replacing a plain "Download filename"
 * text link. There's no real content preview to render for arbitrary files
 * (unlike AttachmentImagePreview's thumbnail), so the icon is the preview.
 */
function FileAttachmentCard({
  filename,
  sizeBytes,
  onPress
}: {
  filename: string;
  sizeBytes: number;
  onPress: () => void;
}) {
  const isPdf = filename.split(".").pop()?.toLowerCase() === "pdf";

  return (
    <button
      type="button"
      onClick={onPress}
      className="mt-1.5 flex w-full items-center gap-2.5 overflow-hidden rounded-lg border border-slate-700 bg-slate-950/60 px-2.5 py-2 text-left transition-colors duration-150 hover:bg-slate-900/80"
    >
      <span className="relative inline-flex shrink-0 leading-none">
        <span className="text-2xl leading-none">{fileIconFor(filename)}</span>
        {isPdf && (
          <span className="absolute -bottom-1 -right-1.5 rounded bg-red-600 px-1 text-[0.55rem] font-bold leading-tight text-white">
            PDF
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-slate-100">{filename}</span>
        <span className="block text-[0.7rem] text-slate-400">{Math.round(sizeBytes / 1024)} KB</span>
      </span>
    </button>
  );
}

/**
 * Inline WhatsApp-style thumbnail for image attachments. Attachments are
 * E2EE, so this can't just point an `<img>` at the presigned S3 URL (that
 * would render raw ciphertext) — it fetches the encrypted bytes, decrypts
 * them client-side with the same `chatKey` as messages, and renders the
 * result as an object URL. `cache` is owned by the parent Chat instance (same
 * pattern as `linkPreviewCacheRef`) so scrolling past the same attachment
 * repeatedly only ever fetches+decrypts once per attachment per Chat mount.
 * Renders nothing on failure — the "Download" link/button next to it (see
 * the attachment render block below) remains the fallback either way.
 */
function AttachmentImagePreview({
  attachment,
  nonce,
  chatKey,
  cache
}: {
  attachment: NonNullable<ApiMessage["attachment"]>;
  nonce: string | null;
  chatKey: string;
  cache: Map<string, string | null>;
}) {
  const [url, setUrl] = useState<string | null | undefined>(() => cache.get(attachment.id));

  useEffect(() => {
    if (cache.has(attachment.id)) {
      setUrl(cache.get(attachment.id) ?? null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const meta = await getAttachment(attachment.id);
        const response = await fetch(meta.url);
        if (!response.ok) throw new Error("fetch_failed");
        const bytes = new Uint8Array(await response.arrayBuffer());
        const decrypted = nonce ? decryptBytes(bytes, nonce, chatKey) : bytes;
        if (!decrypted) throw new Error("decrypt_failed");
        const blob = new Blob([decrypted as unknown as BlobPart], { type: meta.contentType || "image/jpeg" });
        const objectUrl = URL.createObjectURL(blob);
        cache.set(attachment.id, objectUrl);
        if (!cancelled) setUrl(objectUrl);
      } catch {
        cache.set(attachment.id, null);
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment.id, nonce, chatKey, cache]);

  if (url === null) return null;
  if (!url) {
    return <div className="mt-1.5 h-32 w-32 animate-pulse rounded-lg bg-slate-800" aria-hidden />;
  }
  return (
    <img
      src={url}
      alt={attachment.filename}
      className="mt-1.5 max-h-64 max-w-full rounded-lg object-contain"
      onClick={(e) => e.stopPropagation()}
    />
  );
}

/** Dedupe-by-id merge, kept chronologically ascending. Shared by the initial
 * load, WS live-append, scroll-up pagination, and the full-history loader
 * (search/attachments) so none of them can leave the list unsorted or with
 * a duplicate row when their ranges overlap. */
function mergeMessages(incoming: DisplayMessage[], existing: DisplayMessage[]): DisplayMessage[] {
  const byId = new Map<string, DisplayMessage>();
  for (const m of existing) byId.set(m.id, m);
  for (const m of incoming) {
    const prev = byId.get(m.id);
    if (!prev || m.updatedAt >= prev.updatedAt) byId.set(m.id, m);
  }
  return Array.from(byId.values()).sort((a, b) => a.createdAt - b.createdAt);
}

const Chat = forwardRef<ChatHandle, ChatProps>(function Chat({ session, onMenuStateChange }, ref) {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [noMoreOlder, setNoMoreOlder] = useState(false);
  const [loadingFullHistory, setLoadingFullHistory] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAttachments, setShowAttachments] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const pendingScrollAdjustRef = useRef<{ previousScrollHeight: number; previousScrollTop: number } | null>(null);
  const shouldScrollToBottomRef = useRef(false);
  const fullHistoryStartedRef = useRef(false);
  const stopWsRef = useRef<(() => void) | null>(null);
  // Per-Chat-instance link-preview cache, keyed by URL (see LinkPreviewCard).
  const linkPreviewCacheRef = useRef(new Map<string, LinkPreview | null>());
  // Per-Chat-instance decrypted-image cache, keyed by attachment id (see
  // AttachmentImagePreview) — object URLs, so they live only as long as this
  // Chat instance does.
  const attachmentImageCacheRef = useRef(new Map<string, string | null>());
  // Gates the message-entrance animation (see the `bridge-msg-in` keyframes
  // in index.css) to only genuinely-new messages — own sends and
  // WS-received — never the cold-open initial history load, scroll-up
  // pagination, or the search/attachments full-history backfill. Ids are
  // added synchronously (not via a render-time effect) for the initial
  // load, pagination, and full-history paths since those all know upfront
  // their messages are "old"; the catch-all effect below (keyed on
  // `messages`) marks any remaining ids (own sends, WS-received) as seen
  // once they've had a render to play their entrance animation.
  const seenIdsRef = useRef(new Set<string>());

  // Initial load: most recent page (no `since`/`before` — see the server's
  // bug-fix note in `server/src/routes/messages.ts`), then seed `ws.ts`'s
  // live-update catch-up from the latest loaded message's `createdAt` so it
  // doesn't re-fetch the whole history from scratch on connect.
  useEffect(() => {
    let cancelled = false;

    function mergeFromApi(apiMessages: ApiMessage[]) {
      setMessages((prev) => mergeMessages(apiMessages.map((m) => decodeIncoming(m, session)), prev));
    }

    async function init() {
      let initialSince = 0;
      let initialChangesCursor = 0;
      try {
        const result = await listMessagesPage({});
        if (cancelled) return;
        const decoded = result.messages.map((m) => decodeIncoming(m, session)).sort((a, b) => a.createdAt - b.createdAt);
        // Always open the chat scrolled to the latest message (like
        // WhatsApp) rather than wherever the initial page's top happens to
        // land — this is the most recent page already, so "latest" is just
        // the bottom of what was just loaded.
        shouldScrollToBottomRef.current = true;
        setMessages(decoded);
        // Cold-open initial page — mark every id seen immediately so none of
        // it plays the new-message entrance animation.
        for (const m of decoded) seenIdsRef.current.add(m.id);
        if (result.messages.length === 0) setNoMoreOlder(true);
        if (decoded.length > 0) {
          initialSince = decoded[decoded.length - 1]!.createdAt;
          initialChangesCursor = Math.max(...decoded.map((m) => m.updatedAt));
        }
      } catch {
        // Initial page fetch failed (e.g. transient network blip) — fall
        // back to starting WS from since=0, same as the old always-fetch
        // behavior, rather than leaving the chat stuck with nothing.
      }
      if (cancelled) return;
      stopWsRef.current = startWs(
        (incoming) => mergeFromApi(incoming),
        undefined,
        initialSince,
        { onMessageUpdate: mergeFromApi },
        initialChangesCursor
      );
    }

    void init();
    return () => {
      cancelled = true;
      stopWsRef.current?.();
      stopWsRef.current = null;
    };
  }, [session]);

  // Catch-all for `seenIdsRef`: marks any id not already recorded (i.e. an
  // own-send or WS-received message, since the initial load/pagination/
  // full-history paths already mark their own ids synchronously above) as
  // seen after it's had a render to play its entrance animation. Runs after
  // every `messages` change; a no-op once an id is already in the set.
  useEffect(() => {
    for (const m of messages) {
      if (!seenIdsRef.current.has(m.id)) seenIdsRef.current.add(m.id);
    }
  }, [messages]);

  // Preserve scroll position on prepend: capture scrollHeight/scrollTop
  // before the older page is merged in, then once the DOM has the new
  // (taller) content, restore an equivalent visual scroll offset so the
  // view doesn't jump.
  useLayoutEffect(() => {
    const pending = pendingScrollAdjustRef.current;
    const el = messageListRef.current;
    if (pending && el) {
      const newScrollHeight = el.scrollHeight;
      el.scrollTop = newScrollHeight - pending.previousScrollHeight + pending.previousScrollTop;
      pendingScrollAdjustRef.current = null;
    } else if (shouldScrollToBottomRef.current && el) {
      shouldScrollToBottomRef.current = false;
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  async function loadOlderMessages() {
    if (loadingOlder || noMoreOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const el = messageListRef.current;
    if (el) {
      pendingScrollAdjustRef.current = { previousScrollHeight: el.scrollHeight, previousScrollTop: el.scrollTop };
    }
    try {
      const oldest = messages[0].createdAt;
      const result = await listMessagesPage({ before: oldest });
      if (result.messages.length === 0) {
        setNoMoreOlder(true);
        pendingScrollAdjustRef.current = null;
        return;
      }
      const decoded = result.messages.map((m) => decodeIncoming(m, session));
      // Mark these ids seen synchronously, before merging them in, so the
      // entrance animation never has a render where they're both present
      // and unseen — relying on the `loadingOlder` flag alone isn't
      // race-proof here since React 18 batches this `setMessages` call with
      // `setLoadingOlder(false)` in the `finally` block below into a single
      // render.
      for (const m of decoded) seenIdsRef.current.add(m.id);
      setMessages((prev) => mergeMessages(decoded, prev));
    } catch {
      pendingScrollAdjustRef.current = null;
    } finally {
      setLoadingOlder(false);
    }
  }

  function handleScroll() {
    const el = messageListRef.current;
    if (!el) return;
    if (el.scrollTop < SCROLL_TOP_THRESHOLD) {
      void loadOlderMessages();
    }
  }

  /**
   * One-time "load the entire conversation" routine shared by search and the
   * attachments view (per the spec — one pagination loop, not duplicated):
   * repeatedly calls the `before`-paginated endpoint from the oldest
   * currently-loaded message (or "now" if nothing loaded yet) until a page
   * comes back empty, merging every page into `messages`. Also flips
   * `noMoreOlder` so scroll-up pagination knows there's nothing left to
   * lazily fetch either — both mechanisms share the same underlying set.
   */
  async function ensureFullHistoryLoaded() {
    if (fullHistoryStartedRef.current) return;
    fullHistoryStartedRef.current = true;
    setLoadingFullHistory(true);
    try {
      let cursor = messages.length > 0 ? messages[0].createdAt : undefined;
      for (;;) {
        const result = await listMessagesPage(cursor !== undefined ? { before: cursor } : {});
        if (result.messages.length === 0) break;
        cursor = result.messages[0].createdAt;
        const decoded = result.messages.map((m) => decodeIncoming(m, session));
        // Same reasoning as loadOlderMessages(): this is a backfill of
        // existing history (search/attachments), not new messages — mark
        // seen up front so it never cascade-animates.
        for (const m of decoded) seenIdsRef.current.add(m.id);
        setMessages((prev) => mergeMessages(decoded, prev));
      }
      setNoMoreOlder(true);
    } finally {
      setLoadingFullHistory(false);
    }
  }

  function handleToggleSearch() {
    setSearchVisible((v) => {
      const next = !v;
      if (next) void ensureFullHistoryLoaded();
      else setSearchQuery("");
      return next;
    });
  }

  function handleToggleAttachments() {
    setShowAttachments((v) => {
      const next = !v;
      if (next) void ensureFullHistoryLoaded();
      return next;
    });
  }

  // Search/attachments are triggered from App.tsx's nav bar (the "⋮" menu
  // next to the Chat/Settings tabs), not from inside this component's own
  // toolbar — this handle lets that parent-level menu drive Chat's state
  // without lifting all of it out of Chat.
  useImperativeHandle(ref, () => ({
    toggleSearch: handleToggleSearch,
    toggleAttachments: handleToggleAttachments
  }));

  useEffect(() => {
    onMenuStateChange?.({ searchVisible, showAttachments });
  }, [searchVisible, showAttachments, onMenuStateChange]);

  /**
   * Parses a leading-slash draft (e.g. "/ping foo bar") into its command
   * ("ping") and args ("foo bar"), dispatches it over the separate plaintext
   * `/api/commands` channel, then stores BOTH the original typed text and
   * the result (or error) as normal encrypted messages via `sendMessage` —
   * so both end up in chat history like anything else, fully encrypted at
   * rest, even though the dispatch round-trip itself briefly went over the
   * unencrypted channel. Never throws: a failed dispatch becomes a visible
   * result message instead of a crashed compose flow.
   */
  async function handleCommand(rawText: string) {
    const withoutSlash = rawText.slice(1);
    const spaceIndex = withoutSlash.search(/\s/);
    const command = (spaceIndex === -1 ? withoutSlash : withoutSlash.slice(0, spaceIndex)).trim();
    const args = spaceIndex === -1 ? "" : withoutSlash.slice(spaceIndex + 1).trim();

    const { ciphertext: cmdCiphertext, nonce: cmdNonce } = encryptMessage(rawText, session.chatKey);
    const cmdSent = await sendMessage({ ciphertext: cmdCiphertext, nonce: cmdNonce, encrypted: true });
    shouldScrollToBottomRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: cmdSent.id,
        mine: true,
        text: rawText,
        createdAt: cmdSent.createdAt,
        updatedAt: cmdSent.createdAt,
        attachment: null,
        attachmentNonce: null,
        isCommandResult: false,
        deleted: false,
        edited: false
      }
    ]);

    let resultText: string;
    if (!command) {
      resultText = "Error: no command given after \"/\".";
    } else {
      try {
        const { result } = await runCommand(command, args);
        resultText = result;
      } catch (err) {
        const message = err instanceof Error ? err.message : "unknown_error";
        resultText =
          message === "unknown_command" ? `Unknown command: /${command}` : `Error running /${command}: ${message}`;
      }
    }

    const { ciphertext: resCiphertext, nonce: resNonce } = encryptMessage(
      `${COMMAND_RESULT_PREFIX}${resultText}`,
      session.chatKey
    );
    const resSent = await sendMessage({ ciphertext: resCiphertext, nonce: resNonce, encrypted: true });
    shouldScrollToBottomRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: resSent.id,
        mine: true,
        text: resultText,
        createdAt: resSent.createdAt,
        updatedAt: resSent.createdAt,
        attachment: null,
        attachmentNonce: null,
        isCommandResult: true,
        deleted: false,
        edited: false
      }
    ]);
  }

  function cancelEdit() {
    setEditingMessageId(null);
    setDraft("");
  }

  async function handleDeleteMessage(m: DisplayMessage) {
    if (!window.confirm("Delete for everyone?")) return;
    const { message } = await deleteMessage(m.id);
    setMessages((prev) => mergeMessages([decodeIncoming(message, session)], prev));
  }

  async function handleSend() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      if (editingMessageId) {
        const { ciphertext, nonce } = encryptMessage(text, session.chatKey);
        const { message } = await editMessage(editingMessageId, { ciphertext, nonce });
        setMessages((prev) => mergeMessages([decodeIncoming(message, session)], prev));
        setEditingMessageId(null);
        setDraft("");
        return;
      }
      if (text.startsWith("/")) {
        await handleCommand(text);
        setDraft("");
        return;
      }
      const { ciphertext, nonce } = encryptMessage(text, session.chatKey);
      // Show the bubble before the network round-trip (send used to feel stuck
      // waiting on Turso + Expo push). Replace the temp id when the server
      // replies; on failure put the draft back.
      const tempId = crypto.randomUUID();
      const optimisticAt = Date.now();
      shouldScrollToBottomRef.current = true;
      setDraft("");
      setMessages((prev) => [
        ...prev,
        {
          id: tempId,
          mine: true,
          text,
          createdAt: optimisticAt,
          updatedAt: optimisticAt,
          attachment: null,
          attachmentNonce: null,
          isCommandResult: false,
          deleted: false,
          edited: false
        }
      ]);
      try {
        const sent = await sendMessage({ ciphertext, nonce, encrypted: true });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? { ...m, id: sent.id, createdAt: sent.createdAt, updatedAt: sent.createdAt }
              : m
          )
        );
      } catch (err) {
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
        setDraft(text);
        throw err;
      }
    } finally {
      setSending(false);
    }
  }

  async function uploadFileAsAttachment(file: File): Promise<void> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const { ciphertext, nonce } = encryptBytes(bytes, session.chatKey);

    // Revision 6: S3 presigned PUT replaces `@vercel/blob/client`'s
    // `upload()` (single-call token+PUT helper) with an explicit two-step
    // request-then-PUT — file bytes still go straight client->storage,
    // never through this app's own server (Decision 1b).
    const { attachmentId, key, uploadUrl } = await requestUploadUrl({
      filename: file.name,
      sizeBytes: bytes.byteLength,
      contentType: "application/octet-stream"
    });
    await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": "application/octet-stream" },
      body: ciphertext as unknown as BlobPart
    });

    const sent = await sendMessage({
      attachment: {
        id: attachmentId,
        url: key,
        pathname: key,
        filename: file.name,
        contentType: file.type || undefined,
        sizeBytes: bytes.byteLength
      },
      nonce,
      encrypted: true
    });
    // Same optimistic-append reasoning as handleSend() above.
    shouldScrollToBottomRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: sent.id,
        mine: true,
        text: "",
        createdAt: sent.createdAt,
        updatedAt: sent.createdAt,
        attachment: {
          id: attachmentId,
          filename: file.name,
          contentType: file.type || null,
          sizeBytes: bytes.byteLength,
          encrypted: true
        },
        attachmentNonce: nonce,
        isCommandResult: false,
        deleted: false,
        edited: false
      }
    ]);
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) return;
    setSending(true);
    try {
      // Sequential, not Promise.all: keeps message order matching the order
      // files were picked in, and avoids bursting the presigned-upload
      // endpoint with a dozen concurrent requests when someone selects a
      // whole album at once.
      for (const file of files) {
        await uploadFileAsAttachment(file);
      }
    } finally {
      setSending(false);
    }
  }

  async function handleDownload(attachment: NonNullable<ApiMessage["attachment"]>, nonce: string | null) {
    try {
      const meta = await getAttachment(attachment.id);
      const response = await fetch(meta.url);
      if (!response.ok) throw new Error(`attachment_fetch_failed:${response.status}`);
      const bytes = new Uint8Array(await response.arrayBuffer());
      const decrypted = nonce ? decryptBytes(bytes, nonce, session.chatKey) : bytes;
      if (!decrypted) {
        window.alert("Failed to decrypt attachment.");
        return;
      }
      const blob = new Blob([decrypted as unknown as BlobPart], { type: meta.contentType ?? "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = meta.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Most commonly a network/CORS failure talking to S3 directly — never
      // fail silently (a bare unhandled rejection here looks to the user
      // like clicking "Download" did nothing at all).
      console.error("attachment download failed:", err);
      window.alert("Failed to download attachment. Check your connection and try again.");
    }
  }

  async function handleCopy(id: string, text: string) {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
  }

  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  async function handleShare(text: string) {
    if (!text) return;
    try {
      await navigator.share({ text });
    } catch {
      // User cancelled the share sheet — nothing to do.
    }
  }

  const trimmedQuery = searchQuery.trim().toLowerCase();
  const visibleMessages = trimmedQuery
    ? messages.filter((m) => m.text.toLowerCase().includes(trimmedQuery))
    : messages;
  const attachmentMessages = messages.filter((m) => m.attachment !== null);

  return (
    <div className="flex flex-1 flex-col rounded-xl border border-slate-800 bg-gray-900 p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {searchVisible && (
          <input
            type="text"
            autoFocus
            value={searchQuery}
            onFocus={() => void ensureFullHistoryLoaded()}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search messages…"
            className="min-w-[10rem] flex-1 rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-1.5 text-sm text-slate-200 transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500/60"
          />
        )}
        {loadingFullHistory && <span className="text-xs text-slate-400">Loading full conversation…</span>}
      </div>

      {showAttachments ? (
        <div className="flex flex-1 flex-col gap-2 overflow-y-auto">
          {attachmentMessages.length === 0 && !loadingFullHistory && (
            <div className="text-sm text-slate-500">No attachments in this conversation yet.</div>
          )}
          {attachmentMessages.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-800 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2 text-sm text-slate-200">
                {!isImageFilename(m.attachment!.filename) && (
                  <span className="text-lg leading-none">{fileIconFor(m.attachment!.filename)}</span>
                )}
                <span className="min-w-0">
                  <span className="block truncate">{m.attachment!.filename}</span>
                  <span className="text-xs text-slate-400">{Math.round(m.attachment!.sizeBytes / 1024)} KB</span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleDownload(m.attachment!, m.attachmentNonce)}
                className="cursor-pointer whitespace-nowrap text-sm text-blue-300 underline"
              >
                Download
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div ref={messageListRef} onScroll={handleScroll} className="flex flex-1 flex-col gap-2 overflow-y-auto">
          {loadingOlder && <div className="text-center text-xs text-slate-500">Loading older messages…</div>}
          {visibleMessages.map((m, i) => {
            // Entrance animation only for genuinely-new messages (own send /
            // WS-received) — never the initial history load or a scroll-up
            // pagination batch (see seenIdsRef above and its population
            // sites in init()/loadOlderMessages()/ensureFullHistoryLoaded()).
            const isNewEntrance = !seenIdsRef.current.has(m.id) && !loadingOlder;
            const showDaySep =
              i === 0 || dayKey(visibleMessages[i - 1]!.createdAt) !== dayKey(m.createdAt);
            return (
            <Fragment key={m.id}>
            {showDaySep && (
              <div className="my-2 self-center rounded-full bg-slate-800 px-3 py-0.5 text-[0.7rem] text-slate-400">
                {dayLabel(m.createdAt)}
              </div>
            )}
            <div
              className={`group relative max-w-[80%] rounded-lg px-3 py-2 ${
                m.mine ? "rounded-br-sm" : "rounded-bl-sm"
              } ${isNewEntrance ? "animate-[bridge-msg-in_180ms_ease-out]" : ""} ${
                m.isCommandResult
                  ? `border border-dashed border-slate-600 bg-slate-950/80 font-mono ${m.mine ? "self-end" : ""}`
                  : m.mine
                    ? "self-end bg-blue-700"
                    : "bg-slate-800"
              }`}
            >
              {m.isCommandResult && (
                <div className="mb-1 text-[0.65rem] uppercase tracking-wide text-emerald-400">⚙ command result</div>
              )}
              {m.deleted ? (
                <div className="italic text-slate-400">🚫 This message was deleted</div>
              ) : (
                <>
                  {m.text && (
                    <>
                      <div className={canShare ? "pr-16 text-slate-100" : "pr-10 text-slate-100"}>
                        {m.isCommandResult ? m.text : renderMarkdown(m.text)}
                      </div>
                      <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                        {canShare && (
                          <button
                            type="button"
                            onClick={() => handleShare(m.text)}
                            title="Share message"
                            className="cursor-pointer rounded px-1 text-xs text-slate-300 transition-colors duration-100 hover:bg-black/20"
                          >
                            ↗
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleCopy(m.id, m.text)}
                          title="Copy message"
                          className="cursor-pointer rounded px-1 text-xs text-slate-300 transition-colors duration-100 hover:bg-black/20"
                        >
                          {copiedId === m.id ? "✓" : "⧉"}
                        </button>
                        {m.text && !m.isCommandResult && !m.attachment && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingMessageId(m.id);
                              setDraft(m.text);
                            }}
                            title="Edit message"
                            className="cursor-pointer rounded px-1 text-xs text-slate-300 transition-colors duration-100 hover:bg-black/20"
                          >
                            ✎
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleDeleteMessage(m)}
                          title="Delete for everyone"
                          className="cursor-pointer rounded px-1 text-xs text-slate-300 transition-colors duration-100 hover:bg-black/20"
                        >
                          🗑
                        </button>
                      </div>
                    </>
                  )}
                  {!m.isCommandResult &&
                    m.text &&
                    (() => {
                      const url = extractFirstUrl(m.text);
                      return url ? <LinkPreviewCard url={url} cache={linkPreviewCacheRef.current} /> : null;
                    })()}
                </>
              )}
              {!m.deleted && m.attachment && (
                <div className="relative">
                  {!m.text && (
                    <div className="absolute right-0 top-0 flex gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => void handleDeleteMessage(m)}
                        title="Delete for everyone"
                        className="cursor-pointer rounded px-1 text-xs text-slate-300 transition-colors duration-100 hover:bg-black/20"
                      >
                        🗑
                      </button>
                    </div>
                  )}
                  {isImageFilename(m.attachment.filename) ? (
                    <>
                      <AttachmentImagePreview
                        attachment={m.attachment}
                        nonce={m.attachmentNonce}
                        chatKey={session.chatKey}
                        cache={attachmentImageCacheRef.current}
                      />
                      <button
                        type="button"
                        onClick={() => handleDownload(m.attachment!, m.attachmentNonce)}
                        className="mt-1 cursor-pointer text-sm text-blue-300 underline"
                      >
                        Download {m.attachment.filename} ({Math.round(m.attachment.sizeBytes / 1024)} KB)
                      </button>
                    </>
                  ) : (
                    <FileAttachmentCard
                      filename={m.attachment.filename}
                      sizeBytes={m.attachment.sizeBytes}
                      onPress={() => handleDownload(m.attachment!, m.attachmentNonce)}
                    />
                  )}
                </div>
              )}
              <div className="mt-0.5 text-[0.7rem] opacity-60">
                {m.edited && !m.deleted && <span className="mr-1">edited</span>}
                {new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            </Fragment>
            );
          })}
        </div>
      )}

      {editingMessageId && (
        <div className="mt-2 flex items-center justify-between rounded-lg border border-slate-700 bg-slate-800/80 px-3 py-1.5 text-sm text-slate-300">
          <span>Editing message</span>
          <button type="button" onClick={cancelEdit} className="cursor-pointer text-slate-400 hover:text-slate-200">
            ✕
          </button>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          placeholder={editingMessageId ? "Edit message…" : "Message"}
          className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200 transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500/60"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending}
          className="relative cursor-pointer whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2.5 text-white disabled:cursor-not-allowed"
        >
          <span className={`transition-opacity duration-[120ms] ${sending ? "opacity-0" : "opacity-100"}`}>
            Send
          </span>
          {sending && (
            <span className="absolute inset-0 flex items-center justify-center transition-opacity duration-[120ms]" aria-hidden>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            </span>
          )}
        </button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange} />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending}
          className="cursor-pointer whitespace-nowrap rounded-lg border border-slate-700 bg-transparent px-4 py-2.5 text-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Attach
        </button>
      </div>
    </div>
  );
});

export default Chat;
